/**
 * TEXT UTILITIES
 * 
 * Handles text normalization, measurement, and wrapping.
 * Supports caching for performance optimization.
 */

import {
  CHARS_PER_LINE,
} from '../../../../Config/ExistingVisaTreeViewConsts.js';

// ── Memoization Caches ────────────────────────────────────────────────────

const cleanTextCache = new Map();
const wrapTextCache = new Map();
const CACHE_MAX_SIZE = 30;

// ── Text Cleaning ────────────────────────────────────────────────────────

/**
 * Remove HTML tags and collapse whitespace for consistent layout measurement.
 * Results are memoized for performance.
 * 
 * @param {string} text - Raw text with potential HTML tags
 * @returns {string} Cleaned, normalized text
 */
export const cleanText = (text) => {
  if (!text) return '';
  
  if (cleanTextCache.has(text)) {
    return cleanTextCache.get(text);
  }
  
  const cleaned = text
    .replace(/<\/(p|div|li|blockquote|article|section|header|footer|h[1-6]|ul|ol)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  if (cleanTextCache.size >= CACHE_MAX_SIZE) {
    const firstKey = cleanTextCache.keys().next().value;
    cleanTextCache.delete(firstKey);
  }
  cleanTextCache.set(text, cleaned);
  console.log('cleaned',[text,cleaned]);
  return cleaned;
};

// ── Text Wrapping ────────────────────────────────────────────────────────

/**
 * Split text into word-preserving lines that fit within a character limit.
 * Results are memoized for performance.
 * 
 * @param {string} text - Text to wrap
 * @param {number} maxChars - Maximum characters per line
 * @returns {string[]} Array of wrapped lines
 */
export const wrapText = (text, maxChars = CHARS_PER_LINE) => {
  if (!text) return [];
  
  const cacheKey = `${maxChars}|${text}`;
  if (wrapTextCache.has(cacheKey)) {
    return wrapTextCache.get(cacheKey);
  }
  
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (test.length <= maxChars) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  
  if (wrapTextCache.size >= CACHE_MAX_SIZE) {
    const firstKey = wrapTextCache.keys().next().value;
    wrapTextCache.delete(firstKey);
  }
  wrapTextCache.set(cacheKey, lines);
  
  return lines;
};

// ── Cache Management ────────────────────────────────────────────────────

export const clearTextCaches = () => {
  cleanTextCache.clear();
  wrapTextCache.clear();
};

export const getTextCacheStats = () => ({
  cleanText: cleanTextCache.size,
  wrapText: wrapTextCache.size,
});
